import { z } from 'zod';
import type { EventOfflineOperation } from './vault';

export class EventOfflineDataError extends Error {
  constructor() {
    super('Offline actions contain invalid or unsupported data. Nothing was removed. Export an encrypted recovery file and contact support; keep the original import file if restoring.');
    this.name = 'EventOfflineDataError';
  }
}
const identifier = z.string().min(1);
const timestamp = z.iso.datetime();
const envelope = z.object({
  operationId: identifier, kind: z.string().refine(value => !!value.trim()), payload: z.unknown(),
  state: z.enum(['pending', 'completed', 'needs-review']), capturedAt: timestamp,
  sequence: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER).optional(),
  attempts: z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER), message: z.string().optional(),
}).passthrough();

export function validateOperation(value: unknown, recordOperationId?: string): asserts value is EventOfflineOperation {
  const result = envelope.safeParse(value);
  if (!result.success || !Object.hasOwn(result.data, 'payload') || result.data.payload === undefined || (recordOperationId !== undefined && result.data.operationId !== recordOperationId)) throw new EventOfflineDataError();
}

const optionalText = z.string().nullish();
const base = z.object({ commandId: identifier, observedAt: timestamp.nullish(), deviceId: optionalText }).passthrough();
const personAction = base.extend({ attendeeId: identifier, activityId: identifier, source: optionalText, notes: optionalText });
const contact = base.extend({ name: z.string(), email: optionalText, company: optionalText, phone: optionalText, notes: optionalText,
  relationship: z.enum(['Auto', 'Partner', 'Prospect', 'Other', 'Vendor']).optional(), isPrivate: z.boolean().optional(),
  accountId: optionalText, hubspotContactId: optionalText, badgeId: optionalText, activityId: optionalText });
const text = (limit: number) => z.string().max(limit);
const nullableText = (limit: number) => text(limit).nullable().default(null);
const nullableDate = timestamp.nullable().default(null);
const nullableInteger = z.number().int().min(-2147483648).max(2147483647).nullable().default(null);
const displayedItem = z.object({ itemPriceId: text(200), itemType: text(100), quantity: nullableInteger });
const displayedProduct = z.object({
  name: text(256), planId: nullableText(200), status: text(100), quantity: nullableInteger,
  currentTermEnd: nullableDate, source: text(256), refreshedAt: timestamp,
  subscriptionId: nullableText(200), termStart: nullableDate, items: z.array(displayedItem).max(20).nullable().default(null),
});
const displayedEnrichment = z.object({
  source: text(256), syncedAt: nullableDate, lastLoginAt: nullableDate, partnerTier: nullableText(100),
  products: z.array(z.object({ code: text(100), relationshipStatus: nullableText(100),
    hasActiveTenant: z.boolean().nullable().default(null), subscriptionUnits: nullableInteger,
    planUsagePercent: z.number().min(-7.922816251426433e28).max(7.922816251426433e28).nullable().default(null),
  })).max(20),
});
const displayedContext = z.object({
  schemaVersion: z.literal(1), availability: z.enum(['Available', 'Unavailable', 'DirectoryOnly']),
  capturedAt: timestamp, source: text(256), refreshedAt: nullableDate,
  contact: z.object({ name: text(200), email: text(256), company: text(500), phone: text(100), relationship: text(30), notes: nullableText(10000) }),
  accountName: nullableText(500), accountOwnerEmail: nullableText(256), products: z.array(displayedProduct).max(20),
  enrichment: displayedEnrichment.nullable().default(null), truncated: z.boolean().default(false),
}).refine(value => {
  // Include DTO defaults and conservatively budget server-escaped Unicode; validation never rewrites the queued payload.
  const serialized = JSON.stringify(value).replace(/[\u007f-\uffff<>&'+`]/g, character => `\\u${character.charCodeAt(0).toString(16).padStart(4, '0')}`);
  return new TextEncoder().encode(serialized).byteLength <= 65536;
});
const schemas: Record<string, z.ZodType> = {
  attendance: personAction.extend({ status: z.enum(['Attended', 'NoShow']).optional() }),
  registrations: personAction.extend({ status: z.enum(['Registered', 'Waitlisted', 'Cancelled']).optional(), overrideReason: optionalText, testMode: z.boolean().optional() }),
  interactions: personAction.extend({ outcomes: z.array(z.object({ kind: z.enum(['Demo', 'Information', 'AccountManager', 'Support', 'Touchpoint']), product: optionalText, products: z.array(z.string().max(200)).max(10).optional(), request: optionalText, bookingMode: z.enum(['Now', 'Later']).nullish(), urgency: z.enum(['Normal', 'Urgent']).optional() }).passthrough()).min(1), displayedContext: displayedContext.nullish(), testMode: z.boolean().optional() }),
  exceptions: base.extend({ category: z.enum(['Technical', 'Recoverable']), kind: identifier, notes: optionalText, imageData: optionalText, attendeeId: optionalText, activityId: optionalText }),
  'resolve-selection': base.extend({ badge: z.string(), attendeeId: identifier, activityId: optionalText }),
  attendees: contact,
};
const request = z.object({ resource: identifier, method: z.enum(['post', 'put']), input: z.record(z.string(), z.unknown()) }).passthrough();
export function validateEventQueuedOperation(operation: EventOfflineOperation): void {
  const parsed = request.safeParse(operation.payload);
  if (!parsed.success) throw new EventOfflineDataError();
  const { resource, method, input } = parsed.data;
  const contactPath = /^attendees\/[0-9a-f-]+$/.test(resource);
  const sharePath = /^attendees\/[0-9a-f-]+\/share$/.test(resource);
  const summaryPath = /^attendees\/[0-9a-f-]+\/self-summary$/.test(resource);
  const schema = contactPath ? contact : sharePath ? base.extend({ relationship: z.enum(['Partner', 'Prospect']) }) : summaryPath ? base : schemas[resource];
  if (operation.kind !== resource || input.commandId !== operation.operationId || method !== (contactPath ? 'put' : 'post') || !schema?.safeParse(input).success) throw new EventOfflineDataError();
}
